<template>
  <div class="flex flex-col gap-4 w-full">
    <div class="flex flex-col px-3 py-5 gap-8">
      <div class="flex justify-start items-center gap-8">
        <div class="flex justify-start items-center gap-0">
          <back-button
            @onClick="$router.back()"
            variant="secondary"
            style="padding: 0"
          />
          <h1 class="text-xl text-left font-extrabold">Time Off</h1>
        </div>
        <breadcrumb :items="breadcrumbs" />
      </div>
      <c-card class="flex flex-col gap-6 py-3 px-3 w-full">
        <div class="grid grid-cols-3 gap-4 w-full">
           <DashboardCard
            title="Total Requested"
            :value="pendingRequests.length"
            icon="file-queue"
            background-color="#FCF4FF"
          />
           <DashboardCard
            title="Total Approved"
            :value="approvedRequests.length"
            icon="file-checked"
          />
           <DashboardCard
            title="Total Taken"
            :value="0"
            icon="file-solid"
          />
        </div>
        <template v-slot:footer>
          <CardFooter
            search-placeholder="Search by leave type or details"
            @actionModal="isOpenFilter = true"
            @sortType="$queryBuilder({ sort: $event }, getLeaveRequests)"
            @searchResult="$queryBuilder({ search: $event }, getLeaveRequests)"
          >
          </CardFooter>
        </template>
      </c-card>

      <c-table
        :headers="headers"
        :items="leaveRequests"
        aria-label="annual leave plan table"
        :loading="isFetching"
        :has-number="false"
        :page-sync="true"
        :pagination-list="pagination"
        @page="$queryBuilder({ page: $event }, getLeaveRequests)"
        @itemsPerPage="$queryBuilder({ perPage: $event }, getLeaveRequests)"
        v-if="isFetching || leaveRequests"
      >
        <template v-slot:item="{ item }">
          <span
            v-if="item.createdAt"
            class="text-darkPurple text-sm font-normal"
          >
            {{ $DATEFORMAT(new Date(item.data.createdAt), "MMMM dd, yyyy") }}
          </span>
          <span
            v-else-if="item.category"
            class="text-darkPurple text-sm font-normal"
          >
            {{ item.data.category.name }}
          </span>
          <div
            v-else-if="item.requestStartDate"
            class="flex flex-col justify-start gap-1"
          >
            <div class="text-darkPurple text-sm font-semibold">
              {{
                $DATEFORMAT(
                  new Date(item.data.requestStartDate),
                  "MMMM dd, yyyy"
                )
              }}
              <span class="text-carrotOrange">to</span><br />
              {{
                $DATEFORMAT(new Date(item.data.requestEndDate), "MMMM dd, yyyy")
              }}
            </div>
          </div>
          <span v-if="item.daysRequested" class="text-sm">
            {{ item.data.daysRequested }} Day(s)
          </span>
          <span
            v-if="item.leaveBalanceAtRequest"
            class="text-darkPurple text-sm font-normal"
          >
            {{ item.data.leaveBalanceAtRequest ? `${item.data.leaveBalanceAtRequest} days` : '---' }}
          </span>
          <div
            v-if="item.approvalStatus"
            class="rounded-md p-2 text-sm w-28 text-center capitalize"
            :class="{
              'bg-green-100 text-mediumSeaGreen':
                item.data.approvalStatus === 'approved',
              'bg-yellow-100 text-carrotOrange':
                item.data.approvalStatus === 'pending',
              'bg-red-100 text-flame':
                item.data.approvalStatus === 'disapproved',
              'bg-red-100 text-flame': item.data.approvalStatus === 'cancelled',
            }"
          >
            {{ item.data.approvalStatus }}
          </div>
        </template>
      </c-table>

      <div v-else class="flex flex-col justify-center items-center gap-0 px-10">
        <c-icon
          icon-name="paygrade_Illustration"
          size=""
          class-name="w-56 h-56"
        />
        <p class="font-normal text-base leading-5 text-jet text-center w-1/2">
          Leave requests can be found here. This is empty now, but you can start
          adding leave requests to populate this area.
        </p>
      </div>

      <FilterTable
        v-if="isOpenFilter"
        :filter-data="filterOptions"
        :show-search="false"
        @close="isOpenFilter = false"
        @applyFilter="onSubmitFilter($event)"
      />

      <c-modal v-if="isOpenDeleteModal">
        <c-card class="flex flex-col gap-5 max-w-max py-3 px-5">
          <p
            class="text-jet text-base font-bold border-b border-solid border-romanSilver"
          >
            Attention Required!
          </p>
          <div class="flex flex-col justify-start items-start gap-0">
            <p class="text-jet text-base">
              Are you sure you want to delete this request?
            </p>
            <p class="text-jet text-base">This cannot be undone.</p>
          </div>
          <div class="w-full flex justify-center gap-4">
            <c-button
              label="Cancel"
              variant="secondary c-button btn-border"
              :disabled="isDeletingRequest"
              @onClick="isOpenDeleteModal = false"
            />
            <c-button
              variant="primary btn-bg c-button"
              :disabled="isDeletingRequest"
              :label="isDeletingRequest ? 'Deleting...' : 'Delete'"
              @onClick="onCancelLeaveRequest(selectedItem)"
            />
          </div>
        </c-card>
      </c-modal>

      <AddLeaveRequest ref="createRequestRef" @submit="getLeaveRequests" />
      <ViewLeaveRequest ref="viewRequestRef" />
      <GetLeaveBackRequest ref="getLeaveBackRef" @success="getLeaveRequests" />
      <ApprovalStatus ref="approvalStatusRef" :mode="mode" />
    </div>
  </div>
</template>

<script>
import BackButton from "@scelloo/cloudenly-ui/src/components/back-button";
import Breadcrumb from "@scelloo/cloudenly-ui/src/components/bread-crumb";
import CButton from "@scelloo/cloudenly-ui/src/components/button";
import CTable from "@scelloo/cloudenly-ui/src/components/table";
import CCard from "@scelloo/cloudenly-ui/src/components/card";
import CMenu from "@scelloo/cloudenly-ui/src/components/Menu";
import CIcon from "@/components/Icon";
import CModal from "@/components/Modal";
import CardFooter from "@/components/CardFooter";
import FilterTable from "@/components/FilterTable";
import ApprovalStatus from "@/components/ApprovalStatus";
import DashboardCard from "@/components/DashboardCard";

export default {
  components: {
    Breadcrumb,
    BackButton,
    CCard,
    CButton,
    CTable,
    CIcon,
    CMenu,
    CModal,
    CardFooter,
    FilterTable,
    ApprovalStatus,
    DashboardCard,

    AddLeaveRequest: () => import("./sideActions/AddLeaveRequest"),
    ViewLeaveRequest: () => import("./sideActions/ViewLeaveRequest"),
    GetLeaveBackRequest: () => import("./sideActions/GetLeaveBack"),
  },
  data() {
    return {
      isOpenFilter: false,
      isFetching: false,
      isDeletingRequest: false,
      isOpenDeleteModal: false,
      setQueryTimeout: null,
      queryParams: {},
      selectedItem: {},
      leaveRequests: [],
      employeeInfo: {},
      pendingRequests: [],
      approvedRequests: [],
      disapprovedRequests: [],
      cancelledRequests: [],
      pagination: {},
      headers: [
        { title: "Request Date", value: "createdAt" },
        { title: "Leave Type", value: "category" },
        { title: "Duration", value: "requestStartDate" },
        { title: "Total days taken (to date)", value: "daysRequested" },
        { title: "Leave Balance", value: "leaveBalanceAtRequest" },
        { title: "status", value: "approvalStatus" },
      ],
      filterOptions: [
        {
          header: "Status",
          value: "status",
          optionsBox: [
            { name: "Pending", id: "pending" },
            { name: "Approved", id: "approved" },
            { name: "Disapproved", id: "disapproved" },
            { name: "Cancelled", id: "cancelled" },
          ],
        },
      ],
    };
  },
  props: {
    mode: {
      type: String,
      default: "admin",
    },
  },
  computed: {
    breadcrumbs() {
      return [
        { disabled: false, text: "Leave", href: "Leave", id: "Leave" },
        {
          disabled: false,
          text: "Year End Leave Balances",
          id: "Year End Leave Balances",
        },
        {
          disabled: false,
          text: `View Leave History`,
          id: `View Leave History`,
        },
      ];
    },
  },
  methods: {
    onSubmitFilter(options) {
      this.$queryBuilder(options, this.getLeaveRequests);
      this.isOpenFilter = false;
    },
    onCancelLeaveRequest(request) {
      this.isDeletingRequest = true;
      this.$_cancelLeaveRequest(request.id)
        .then(({ data }) => {
          this.$toasted.success(data.message, { duration: 3000 });
          this.getLeaveRequests();
          this.isDeletingRequest = false;
          this.isOpenDeleteModal = false;
        })
        .catch((error) => {
          this.$toasted.error(error.message, { duration: 3000 });
          this.getLeaveRequests();
          this.isDeletingRequest = false;
        });
    },
    async getLeaveRequests(params) {
      this.isFetching = true;

      const payload = { employeeId: this.$route.params.id, params };
      await this.$_getEmployeeLeaveRequest(payload)
        .then(({ data }) => {
          this.leaveRequests = data.requests;
          this.employeeInfo = data.employee;
          this.pendingRequests = data.requests?.filter(
            (v) => v?.approvalStatus === "pending"
          );
          this.approvedRequests = data.requests?.filter(
            (v) => v?.approvalStatus === "approved"
          );
          this.disapprovedRequests = data.requests?.filter(
            (v) => v?.approvalStatus === "disapproved"
          );
          this.cancelledRequests = data.requests?.filter(
            (v) => v?.approvalStatus === "cancelled"
          );
          this.pagination = data.meta;

          this.isFetching = false;
        })
        .catch(() => {
          // const message = "Something went wrong, try again!"
          // this.$toasted.error(error || message, { duration: 5000 })
          this.$toasted.error(
            "Error fetching data, please check your network.",
            { duration: 3000 }
          );
        });
    },
    async bootstrapModule() {
      this.isLoading = true;
      await this.getLeaveRequests("");
      this.isLoading = false;
    },
  },
  created() {
    this.bootstrapModule();
  },
};
</script>

<style scoped>
::v-deep .select-class span select {
  margin-top: 3px !important;
  height: 42px !important;
}
.button-style {
  font-weight: 600 !important;
  font-size: 16px !important;
  font-size: 16px !important;
  line-height: 120% !important;
  padding: 10px 50px !important;
}

.btn-bg {
  background-color: var(--dynamic-Background) !important;
}

.btn-border {
  color: var(--dynamic-Background) !important;
  border: 1px solid var(--dynamic-Background) !important;
}
</style>
